import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SchemaRoundedIcon from '@mui/icons-material/SchemaRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import axiosInstance from '../utils/axiosInstance';
import {
  compatibleCommandTargets,
  executionProfilePayload,
  localExecutionProfileStatus,
  mappingsByCapabilityInput,
  normalizeAdvertisedCommand,
} from '../utils/capabilityExecutionProfiles';
import { formatCapabilityLabel } from '../utils/capabilityContract';


function endpointFor(partnerId, capabilityId, assetId) {
  return `/partners/${encodeURIComponent(partnerId)}/capabilities/${encodeURIComponent(capabilityId)}/assets/${encodeURIComponent(assetId)}/execution-profile`;
}

function statusPresentation(status) {
  if (status === 'ready') return { label: 'Ready', color: 'success' };
  if (status === 'incomplete') return { label: 'Incomplete', color: 'warning' };
  if (status === 'requires_attention') return { label: 'Requires Attention', color: 'error' };
  return { label: 'Not configured', color: 'default' };
}

function fieldTypeLabel(field) {
  return formatCapabilityLabel(field.field_type || field.type || 'text');
}

function FieldMetadata({ field, commandSide = false }) {
  const options = Array.isArray(field?.options) ? field.options : [];
  const acceptedTypes = field?.accepted_media_types || field?.accepted_content_types || [];
  return (
    <Stack spacing={0.35}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {field?.display_name || field?.name || field?.key}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {commandSide
          ? `${field?.kind === 'parameter' ? 'Command parameter' : 'Input artifact'} · ${fieldTypeLabel(field)} · ${field?.required ? 'Required' : 'Optional'}`
          : `${field?.source_label} · ${fieldTypeLabel(field)} · ${field?.required ? 'Required' : 'Optional'}`}
      </Typography>
      {field?.description ? (
        <Typography variant="caption" color="text.secondary">{field.description}</Typography>
      ) : null}
      {options.length ? (
        <Typography variant="caption" color="text.secondary">Options: {options.join(', ')}</Typography>
      ) : null}
      {acceptedTypes.length ? (
        <Typography variant="caption" color="text.secondary">Accepted formats: {acceptedTypes.join(', ')}</Typography>
      ) : null}
    </Stack>
  );
}

function validInitialMappings(profile, fields, command) {
  if (!profile || !command) return {};
  const initial = mappingsByCapabilityInput(profile);
  return Object.fromEntries(fields.flatMap((field) => {
    const mapping = initial[field.key];
    if (!mapping) return [];
    const compatible = compatibleCommandTargets(field, command);
    return compatible.some((target) => target.key === mapping.targetKey)
      ? [[field.key, mapping]]
      : [];
  }));
}

function ExecutionProfileDialog({
  open,
  asset,
  commands,
  capabilityInputs,
  profile,
  saving,
  persisted,
  onClose,
  onSave,
  onClear,
}) {
  const [selectedCommandIdentifier, setSelectedCommandIdentifier] = useState('');
  const [mappings, setMappings] = useState({});
  const [commandChanged, setCommandChanged] = useState(false);
  const normalizedCommands = useMemo(
    () => (commands || []).map(normalizeAdvertisedCommand),
    [commands],
  );
  const selectedCommand = normalizedCommands.find(
    (command) => command.command_identifier === selectedCommandIdentifier,
  ) || null;

  useEffect(() => {
    if (!open) return;
    const commandIdentifier = profile?.selected_command_identifier || profile?.command_key || '';
    const initialCommand = normalizedCommands.find(
      (command) => command.command_identifier === commandIdentifier,
    ) || null;
    setSelectedCommandIdentifier(commandIdentifier);
    setMappings(validInitialMappings(profile, capabilityInputs, initialCommand));
    setCommandChanged(false);
  }, [open, profile, capabilityInputs, normalizedCommands]);

  const mappedTargetKeys = new Set(
    Object.values(mappings).filter(Boolean).map((mapping) => `${mapping.kind}:${mapping.targetKey}`),
  );
  const requirements = selectedCommand?.inputs || [];
  const invalidMappings = profile?.invalid_mappings || [];

  const selectCommand = (identifier) => {
    if (identifier !== selectedCommandIdentifier && selectedCommandIdentifier) {
      setCommandChanged(true);
    }
    setSelectedCommandIdentifier(identifier);
    setMappings({});
  };

  const updateMapping = (field, targetKey) => {
    setMappings((current) => {
      const next = { ...current };
      if (!targetKey) {
        delete next[field.key];
      } else {
        next[field.key] = {
          kind: field.is_artifact ? 'artifact' : 'parameter',
          targetKey,
        };
      }
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Execution Profile · {asset?.asset_name || 'Asset'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Alert severity="info">
            Optional. This maps capability request values directly into one command; manual asset execution remains available.
          </Alert>
          {profile?.requires_attention || invalidMappings.length ? (
            <Alert severity="warning">
              The capability or command schema has changed. Review the mappings below before this profile can execute.
            </Alert>
          ) : null}
          {commandChanged ? (
            <Alert severity="warning">The selected command changed, so its previous mappings were cleared. Remap the command inputs.</Alert>
          ) : null}
          <TextField
            select
            fullWidth
            label="Advertised Command"
            value={selectedCommandIdentifier}
            onChange={(event) => selectCommand(event.target.value)}
            helperText="Only commands currently advertised by this asset are available."
          >
            <MenuItem value=""><em>Select a command</em></MenuItem>
            {selectedCommandIdentifier && !selectedCommand ? (
              <MenuItem value={selectedCommandIdentifier} disabled>
                {selectedCommandIdentifier} — no longer advertised
              </MenuItem>
            ) : null}
            {normalizedCommands.map((command) => (
              <MenuItem key={command.command_identifier} value={command.command_identifier}>
                {command.display_name || command.command_identifier}
              </MenuItem>
            ))}
          </TextField>

          {selectedCommand ? (
            <>
              {selectedCommand.description ? (
                <Typography variant="body2" color="text.secondary">{selectedCommand.description}</Typography>
              ) : null}
              <Box>
                <Typography variant="overline" color="text.secondary">Direct input mappings</Typography>
                <Stack divider={<Divider flexItem />} sx={{ mt: 0.5 }}>
                  {capabilityInputs.length ? capabilityInputs.map((field) => {
                    const compatibleTargets = compatibleCommandTargets(field, selectedCommand);
                    const mapping = mappings[field.key];
                    const selectedTarget = compatibleTargets.find((target) => target.key === mapping?.targetKey);
                    return (
                      <Stack
                        key={field.key}
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        sx={{ py: 1.5 }}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}><FieldMetadata field={field} /></Box>
                        <Typography color="text.secondary" sx={{ textAlign: 'center' }}>→</Typography>
                        <Box sx={{ flex: 1.15, minWidth: 0 }}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label={field.is_artifact ? 'Input artifact' : 'Command parameter'}
                            value={mapping?.targetKey || ''}
                            onChange={(event) => updateMapping(field, event.target.value)}
                            helperText={compatibleTargets.length ? 'Compatible targets only' : 'No compatible command input'}
                          >
                            <MenuItem value=""><em>Not mapped</em></MenuItem>
                            {compatibleTargets.map((target) => {
                              const identity = `${field.is_artifact ? 'artifact' : 'parameter'}:${target.key}`;
                              const usedElsewhere = mappedTargetKeys.has(identity) && mapping?.targetKey !== target.key;
                              return (
                                <MenuItem key={target.key} value={target.key} disabled={usedElsewhere}>
                                  {target.display_name || target.key} — {fieldTypeLabel(target)}{target.required ? ' · Required' : ''}
                                </MenuItem>
                              );
                            })}
                          </TextField>
                          {selectedTarget ? <Box sx={{ mt: 0.75 }}><FieldMetadata field={selectedTarget} commandSide /></Box> : null}
                        </Box>
                      </Stack>
                    );
                  }) : (
                    <Alert severity="warning" sx={{ my: 1.5 }}>This capability has no eligible request inputs.</Alert>
                  )}
                </Stack>
              </Box>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Command Requirements</Typography>
                <Stack spacing={0.75}>
                  {requirements.length ? requirements.map((input) => {
                    const kind = input.kind === 'parameter' ? 'parameter' : 'artifact';
                    const mapped = mappedTargetKeys.has(`${kind}:${input.key}`);
                    return (
                      <Stack key={`${kind}:${input.key}`} direction="row" spacing={1} alignItems="center">
                        {mapped
                          ? <CheckCircleRoundedIcon fontSize="small" color="success" />
                          : <CloseRoundedIcon fontSize="small" color={input.required ? 'error' : 'disabled'} />}
                        <Typography variant="body2">
                          {input.display_name || input.key}: {mapped ? 'mapped' : input.required ? 'required and unmapped' : 'optional and unmapped'}
                        </Typography>
                      </Stack>
                    );
                  }) : <Typography variant="body2" color="text.secondary">This command has no inputs.</Typography>}
                </Stack>
              </Paper>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {profile ? (
            <Button
              color="error"
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={onClear}
              disabled={saving}
            >
              Clear Profile
            </Button>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => onSave(executionProfilePayload(selectedCommandIdentifier, mappings))}
            disabled={saving || !selectedCommandIdentifier}
          >
            {saving ? 'Saving...' : persisted ? 'Save Profile' : 'Save Draft'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default function CapabilityExecutionProfiles({
  assets = [],
  capabilityInputs = [],
  partnerId,
  capabilityId,
  drafts = {},
  onDraftsChange,
  canManage = true,
  fulfillmentMode = 'manual',
}) {
  const persisted = Boolean(partnerId && capabilityId);
  const [records, setRecords] = useState({});
  const [commandsByAsset, setCommandsByAsset] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorAsset, setEditorAsset] = useState(null);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const assetKey = assets.map((asset) => asset.asset_id).filter(Boolean).join('|');
  const automationReady = assets.some((asset) => {
    if (persisted) {
      const record = records[asset.asset_id];
      return record?.profile_status === 'ready' || record?.execution_profile?.is_executable;
    }
    const profile = drafts[asset.asset_id];
    const commands = commandsByAsset[asset.asset_id] || [];
    const selectedCommand = commands.map(normalizeAdvertisedCommand).find(
      (command) => command.command_identifier === profile?.selected_command_identifier,
    );
    return localExecutionProfileStatus(profile, selectedCommand, capabilityInputs).status === 'ready';
  });

  useEffect(() => {
    if (!persisted || !assetKey) {
      setRecords({});
      setLoading(false);
      return undefined;
    }
    let active = true;
    async function loadProfiles() {
      setLoading(true);
      setError('');
      const results = await Promise.allSettled(assets.map(async (asset) => {
        const response = await axiosInstance.get(endpointFor(partnerId, capabilityId, asset.asset_id));
        return [asset.asset_id, response.data];
      }));
      if (!active) return;
      const nextRecords = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled') nextRecords[result.value[0]] = result.value[1];
      });
      setRecords(nextRecords);
      if (results.some((result) => result.status === 'rejected')) {
        setError('One or more execution profiles could not be loaded.');
      }
      setLoading(false);
    }
    loadProfiles();
    return () => { active = false; };
  }, [persisted, partnerId, capabilityId, assetKey]);

  const openEditor = async (asset) => {
    setError('');
    setFeedback('');
    if (persisted && !records[asset.asset_id]) {
      setLoading(true);
      try {
        const response = await axiosInstance.get(endpointFor(partnerId, capabilityId, asset.asset_id));
        setRecords((current) => ({ ...current, [asset.asset_id]: response.data }));
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || 'Unable to load the execution profile.');
        setLoading(false);
        return;
      }
      setLoading(false);
    } else if (!persisted && !commandsByAsset[asset.asset_id]) {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/assets/${encodeURIComponent(asset.asset_id)}/commands`);
        setCommandsByAsset((current) => ({
          ...current,
          [asset.asset_id]: Array.isArray(response.data?.commands) ? response.data.commands : [],
        }));
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || 'Unable to load advertised commands.');
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    setEditorAsset(asset);
  };

  const saveProfile = async (payload) => {
    const assetId = editorAsset.asset_id;
    setSaving(true);
    setError('');
    try {
      if (persisted) {
        const response = await axiosInstance.put(endpointFor(partnerId, capabilityId, assetId), payload);
        setRecords((current) => ({ ...current, [assetId]: response.data }));
        setFeedback('Execution profile saved.');
      } else {
        onDraftsChange?.({ ...drafts, [assetId]: payload });
        setFeedback('Execution profile will be saved when the capability is created.');
      }
      setEditorAsset(null);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to save the execution profile.');
    } finally {
      setSaving(false);
    }
  };

  const clearProfile = async () => {
    const assetId = editorAsset.asset_id;
    setSaving(true);
    setError('');
    try {
      if (persisted) {
        const response = await axiosInstance.delete(endpointFor(partnerId, capabilityId, assetId));
        setRecords((current) => ({ ...current, [assetId]: response.data }));
      } else {
        const next = { ...drafts };
        delete next[assetId];
        onDraftsChange?.(next);
      }
      setFeedback('Execution profile cleared. The asset remains attached.');
      setEditorAsset(null);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to clear the execution profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SchemaRoundedIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Capability Execution Profiles</Typography>
          <Chip size="small" variant="outlined" label="Optional" />
          {loading ? <CircularProgress size={16} /> : null}
        </Stack>
      </Box>
      <Box sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Choose how each attached asset can receive this capability&apos;s request inputs. Assets without a profile remain available for manual execution.
          </Typography>
          {fulfillmentMode === 'automated' && !automationReady && !loading ? (
            <Alert severity="warning">
              No attached asset currently has a valid execution profile. Requests can still be created, but automated fulfillment will enter Awaiting Resource until this is resolved.
            </Alert>
          ) : null}
          {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
          {feedback ? <Alert severity="success" onClose={() => setFeedback('')}>{feedback}</Alert> : null}
          {assets.length ? assets.map((asset) => {
            const record = records[asset.asset_id];
            const profile = persisted ? record?.execution_profile : drafts[asset.asset_id];
            const commands = persisted ? record?.commands || [] : commandsByAsset[asset.asset_id] || [];
            const selectedCommand = commands.map(normalizeAdvertisedCommand).find(
              (command) => command.command_identifier === profile?.selected_command_identifier,
            );
            const status = persisted
              ? record?.profile_status || 'not_configured'
              : localExecutionProfileStatus(profile, selectedCommand, capabilityInputs).status;
            const presentation = statusPresentation(status);
            const parameterCount = profile?.parameter_mappings?.length || 0;
            const artifactCount = profile?.artifact_mappings?.length || 0;
            return (
              <Paper key={asset.asset_id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{asset.asset_name || 'Unnamed Asset'}</Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                      <Chip size="small" label={presentation.label} color={presentation.color} variant={status === 'not_configured' ? 'outlined' : 'filled'} />
                      {profile?.selected_command_identifier ? <Chip size="small" variant="outlined" label={`Command: ${profile.selected_command_identifier}`} /> : null}
                      {profile ? <Chip size="small" variant="outlined" label={`${parameterCount} parameters · ${artifactCount} artifacts`} /> : null}
                    </Stack>
                  </Box>
                  {canManage ? (
                    <Button
                      size="small"
                      variant={profile ? 'outlined' : 'contained'}
                      startIcon={<EditRoundedIcon />}
                      onClick={() => openEditor(asset)}
                      disabled={loading}
                    >
                      {status === 'not_configured' ? 'Configure Execution Profile' : status === 'incomplete' ? 'Continue Configuration' : 'Edit Execution Profile'}
                    </Button>
                  ) : null}
                </Stack>
              </Paper>
            );
          }) : (
            <Typography variant="body2" color="text.secondary">Attach an asset to configure an execution profile.</Typography>
          )}
        </Stack>
      </Box>

      <ExecutionProfileDialog
        open={Boolean(editorAsset)}
        asset={editorAsset}
        commands={editorAsset ? (persisted ? records[editorAsset.asset_id]?.commands : commandsByAsset[editorAsset.asset_id]) : []}
        capabilityInputs={editorAsset && persisted
          ? records[editorAsset.asset_id]?.capability_inputs || capabilityInputs
          : capabilityInputs}
        profile={editorAsset ? (persisted ? records[editorAsset.asset_id]?.execution_profile : drafts[editorAsset.asset_id]) : null}
        saving={saving}
        persisted={persisted}
        onClose={() => setEditorAsset(null)}
        onSave={saveProfile}
        onClear={clearProfile}
      />
    </Paper>
  );
}
